// ============================================================================
// USER MULTI-SELECT COMPONENT
// React-Select based multi-user picker for work item assignment
// Date: October 17, 2025
// ============================================================================

'use client';

import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import { authService } from '@/lib/authService';

// ============================================================================
// TYPES
// ============================================================================

interface User {
  id: number;
  email: string;
  first_name?: string;
  last_name?: string;
  role?: string;
}

interface UserMultiSelectProps {
  value?: number[]; // Array of user IDs
  onChange: (userIds: number[]) => void;
  placeholder?: string;
  className?: string;
  isDisabled?: boolean;
  moduleKey?: string; // Optional: filter users by module access
  allowedUserIds?: number[]; // Optional: restrict to specific user IDs (e.g. self + subordinates)
  teamMemberUserIds?: number[]; // Optional: restrict to team members only
}

// ============================================================================
// COMPONENT
// ============================================================================

export default function UserMultiSelect({
  value = [],
  onChange,
  placeholder = 'Assign to users...',
  className = '',
  isDisabled = false,
  moduleKey,
  allowedUserIds,
  teamMemberUserIds
}: UserMultiSelectProps) {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  
  // Load users with module access
  useEffect(() => {
    const loadUsers = async () => {
      setLoading(true);
      try {
        const token = authService.getTokens()?.access_token;
        if (!token) {
          throw new Error('No auth token');
        }
        
        // If module_key is provided, fetch only users with access to that module
        const url = moduleKey 
          ? `/api/v1/module-access/users?module_key=${moduleKey}`
          : '/api/v1/users';
        
        console.log('[UserMultiSelect] Loading users from:', url, 'moduleKey:', moduleKey);
        
        const response = await fetch(url, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        const data = await response.json();
        console.log('[UserMultiSelect] API response:', data);
        
        if (data.success) {
          setUsers(data.data || data.users || []);
        } else {
          console.error('Failed to load users:', data.error);
        }
      } catch (error) {
        console.error('Error loading users:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadUsers();
  }, [moduleKey]);
  
  // Format user name
  const formatUserName = (user: User): string => {
    const name = [user.first_name, user.last_name].filter(Boolean).join(' ');
    return name || user.email;
  };
  
  // Format options for react-select, respecting allowedUserIds and teamMemberUserIds restrictions
  const options = users
    .filter(u => {
      if (allowedUserIds && !allowedUserIds.includes(u.id)) {
        return false;
      }
      if (teamMemberUserIds && !teamMemberUserIds.includes(u.id)) {
        return false;
      }
      return true;
    })
    .map(u => ({
      value: u.id,
      label: `${formatUserName(u)} (${u.email})`,
      user: u
    }));
  
  // Find selected options
  const selectedOptions = options.filter(o => value.includes(o.value));
  
  // Detect dark mode from document
  const isDarkMode = typeof document !== 'undefined' && document.documentElement.classList.contains('dark');
  
  // Custom styles for react-select with dark mode support
  const customStyles = {
    control: (base: any, state: any) => ({
      ...base,
      minHeight: '42px',
      backgroundColor: isDarkMode ? '#374151' : 'white',
      borderColor: state.isFocused 
        ? '#3b82f6' 
        : isDarkMode ? '#4b5563' : '#d1d5db',
      boxShadow: state.isFocused ? '0 0 0 2px rgba(59, 130, 246, 0.1)' : 'none',
      '&:hover': {
        borderColor: state.isFocused ? '#3b82f6' : isDarkMode ? '#6b7280' : '#9ca3af'
      }
    }),
    menu: (base: any) => ({
      ...base,
      backgroundColor: isDarkMode ? '#1f2937' : 'white',
      borderColor: isDarkMode ? '#374151' : '#e5e7eb'
    }),
    option: (base: any, state: any) => ({
      ...base,
      backgroundColor: state.isSelected 
        ? '#3b82f6' 
        : state.isFocused 
        ? isDarkMode ? '#374151' : '#eff6ff'
        : isDarkMode ? '#1f2937' : 'white',
      color: state.isSelected ? 'white' : isDarkMode ? '#f3f4f6' : '#1f2937',
      '&:active': {
        backgroundColor: '#3b82f6'
      }
    }),
    multiValue: (base: any) => ({
      ...base,
      backgroundColor: isDarkMode ? '#1e3a5f' : '#dbeafe',
      borderRadius: '4px'
    }),
    multiValueLabel: (base: any) => ({
      ...base,
      color: isDarkMode ? '#93c5fd' : '#1e40af',
      fontWeight: 500
    }),
    multiValueRemove: (base: any) => ({
      ...base,
      color: isDarkMode ? '#93c5fd' : '#1e40af',
      ':hover': {
        backgroundColor: '#3b82f6',
        color: 'white'
      }
    }),
    input: (base: any) => ({
      ...base,
      color: isDarkMode ? '#f3f4f6' : '#1f2937'
    }),
    placeholder: (base: any) => ({
      ...base,
      color: isDarkMode ? '#9ca3af' : '#6b7280'
    }),
    singleValue: (base: any) => ({
      ...base,
      color: isDarkMode ? '#f3f4f6' : '#1f2937'
    })
  };
  
  return (
    <div className={className}>
      <Select
        value={selectedOptions}
        onChange={(selected) => {
          const selectedIds = selected ? selected.map(s => s.value) : [];
          onChange(selectedIds);
        }}
        options={options}
        isLoading={loading}
        placeholder={placeholder}
        isMulti
        isClearable
        isSearchable
        isDisabled={isDisabled}
        styles={customStyles}
        noOptionsMessage={() => 'No users found'}
        loadingMessage={() => 'Loading users...'}
        closeMenuOnSelect={false}
      />
    </div>
  );
}
